/**
 * ExplanationPanel.tsx - Tabbed explanation display
 *
 * Displays narrative, structured, and summary explanations.
 * Per wobbly-nibbling-chipmunk.md Part 15.3
 */

import { useMemo } from 'react'
import type { SynthesizedExplanation, ExplanationTab, ChainExplanation } from '@models/oracle'
import './ExplanationPanel.css'

interface ExplanationPanelProps {
  explanation: SynthesizedExplanation
  activeTab: ExplanationTab
  onTabChange: (tab: ExplanationTab) => void
}

// Tab configuration
const TABS: { id: ExplanationTab; label: string }[] = [
  { id: 'narrative', label: 'Narrative' },
  { id: 'structured', label: 'Structured' },
  { id: 'summary', label: 'Summary' },
]

// Strength indicator colors
const STRENGTH_COLORS: Record<string, string> = {
  STRONG: '#10b981',
  MODERATE: '#f59e0b',
  SPECULATIVE: '#f97316',
  WEAK: '#ef4444',
}

// Schema display names for better readability
const SCHEMA_DISPLAY_NAMES: Record<string, string> = {
  'BSC Vertical Causation (cross-perspective causal chains)': 'BSC Vertical Causation',
  'Customer Economics Model (retention-to-revenue causation)': 'Customer Economics',
}

// Group chains by their key_insight (schema) to avoid apparent duplication
interface GroupedChains {
  schema: string
  schemaDescription: string
  chains: ChainExplanation[]
}

function ExplanationPanel({
  explanation,
  activeTab,
  onTabChange,
}: ExplanationPanelProps) {
  const { narrative, structured, summary, chain_explanations, overall_strength } =
    explanation

  // Group chains by schema to avoid showing apparent duplicates
  const groupedChains = useMemo((): GroupedChains[] => {
    const groups = new Map<string, ChainExplanation[]>()

    chain_explanations.forEach((chain) => {
      const schemaKey = chain.key_insight
      if (!groups.has(schemaKey)) {
        groups.set(schemaKey, [])
      }
      groups.get(schemaKey)!.push(chain)
    })

    return Array.from(groups.entries()).map(([schema, chains]) => ({
      schema: SCHEMA_DISPLAY_NAMES[schema] || schema.split(' (')[0],
      schemaDescription: schema,
      chains,
    }))
  }, [chain_explanations])

  // Get unique anchors across all chains
  const uniqueAnchors = useMemo(() => {
    const anchors = new Set(chain_explanations.map((c) => c.anchor))
    return Array.from(anchors)
  }, [chain_explanations])

  return (
    <div className="explanation-panel">
      {/* Tab selector */}
      <div className="explanation-tabs">
        {TABS.map((tab) => (
          <button
            key={tab.id}
            className={`tab-button ${activeTab === tab.id ? 'active' : ''}`}
            onClick={() => onTabChange(tab.id)}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Overall strength indicator */}
      <div className="strength-indicator">
        <span className="strength-label">Inference Strength:</span>
        <span
          className="strength-value"
          style={{ color: STRENGTH_COLORS[overall_strength] }}
        >
          {overall_strength}
        </span>
      </div>

      {/* Tab content */}
      <div className="explanation-content">
        {activeTab === 'narrative' && (
          <div className="explanation-text narrative">
            <pre>{narrative}</pre>
          </div>
        )}

        {activeTab === 'structured' && (
          <div className="explanation-text structured">
            <pre>{structured}</pre>
          </div>
        )}

        {activeTab === 'summary' && (
          <div className="explanation-text summary">
            <p>{summary}</p>
          </div>
        )}
      </div>

      {/* Chain explanations - grouped by schema */}
      {groupedChains.length > 0 && (
        <div className="chain-explanations">
          <div className="chain-summary">
            <h5>Reasoning Summary</h5>
            <p className="chain-summary-text">
              Analyzed <strong>{uniqueAnchors.length}</strong> anchor concept{uniqueAnchors.length !== 1 ? 's' : ''} using{' '}
              <strong>{groupedChains.length}</strong> reasoning pattern{groupedChains.length !== 1 ? 's' : ''}
            </p>
            <div className="anchor-tags">
              {uniqueAnchors.map((anchor) => (
                <span key={anchor} className="anchor-tag">
                  {anchor}
                </span>
              ))}
            </div>
          </div>

          {groupedChains.map((group, groupIdx) => (
            <div key={groupIdx} className="schema-group">
              <div className="schema-header">
                <span className="schema-name">{group.schema}</span>
                <span className="schema-chain-count">
                  {group.chains.length} chain{group.chains.length !== 1 ? 's' : ''}
                </span>
              </div>
              <div className="schema-chains">
                {group.chains.map((chain, chainIdx) => (
                  <div key={chainIdx} className="chain-detail-compact">
                    <span className="chain-anchor-compact">{chain.anchor}</span>
                    <span className="chain-direction-compact">
                      {chain.direction === 'upstream'
                        ? '↑'
                        : chain.direction === 'downstream'
                          ? '↓'
                          : '↔'}
                    </span>
                    <span
                      className="chain-strength-compact"
                      style={{ color: STRENGTH_COLORS[chain.strength] }}
                    >
                      {chain.strength}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

export default ExplanationPanel
